import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import TabNavigation from '../../components/ui/TabNavigation';
import AddMemberModal from './components/AddMemberModal';
import BackupRestoreControls from './components/BackupRestoreControls';
import GoogleCalendarSettings from './components/GoogleCalendarSettings';
import AchievementsPanel from './components/AchievementsPanel';
import ParentalLockOverlay from '../../components/ui/ParentalLockOverlay';
import ParentalLockSettings from './components/ParentalLockSettings';
import { isSessionUnlocked } from '../../utils/parentalLockService';
import { useAuth } from '../../contexts/AuthContext';
import { familyService } from '../../services/familyService';




const FamilySettingsManagement = () => {
  const { user } = useAuth();
  const [familyMembers, setFamilyMembers] = useState([]);
  const [familyGroup, setFamilyGroup] = useState(null);
  const [preferences, setPreferences] = useState(null);
  const [memberAchievements, setMemberAchievements] = useState({});
  const [allTasks, setAllTasks] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(isSessionUnlocked());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const tabs = [
    { id: 'calendar', label: 'Calendar Sync', icon: 'calendar' },
    { id: 'achievements', label: 'Achievements', icon: 'trophy' },
    { id: 'backup', label: 'Backup & Restore', icon: 'database' },
    { id: 'parental-lock', label: 'Parental Lock', icon: 'shield' }
  ];

  useEffect(() => {
    loadFamilyData();
  }, [user]);

  const loadFamilyData = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const familyData = await familyService?.getUserFamily();
      if (familyData) {
        setFamilyGroup(familyData?.family_groups);
        const members = await familyService?.getFamilyMembers(familyData?.family_groups?.id);
        setFamilyMembers(members?.map(m => ({
          id: m?.id,
          name: m?.display_name,
          role: m?.member_role,
          color: m?.color,
          initials: m?.initials,
          familyRole: m?.family_role,
          userId: m?.user_id
        })));
        
        const prefs = await familyService?.getFamilyPreferences(familyData?.family_groups?.id);
        setPreferences({
          notifications: prefs?.notifications,
          defaultView: prefs?.default_view,
          autoSync: prefs?.auto_sync,
          weekStartsOn: prefs?.week_starts_on,
          googleCalendarConnected: prefs?.google_calendar_connected,
          googleCalendarId: prefs?.google_calendar_id,
          googleCalendarAutoImport: prefs?.google_calendar_auto_import,
          googleCalendarTwoWaySync: prefs?.google_calendar_two_way_sync,
          holidayLocation: prefs?.holiday_location,
          showPublicHolidays: prefs?.show_public_holidays,
          showSchoolTerms: prefs?.show_school_terms
        });
      }
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const savedAchievements = localStorage?.getItem('memberAchievements');
    if (savedAchievements) {
      try {
        setMemberAchievements(JSON?.parse(savedAchievements));
      } catch (e) {
        console?.error('Failed to load achievements:', e);
      }
    }

    const dailyTasks = JSON?.parse(localStorage?.getItem('dailyTasks') || '[]');
    const longTermTasks = JSON?.parse(localStorage?.getItem('longTermTasks') || '[]');
    setAllTasks([...dailyTasks, ...longTermTasks]);
  }, []);

  const handleMemberUpdate = async (updatedMember) => {
    try {
      await familyService?.updateFamilyMember(updatedMember?.id, {
        displayName: updatedMember?.name,
        memberRole: updatedMember?.role,
        color: updatedMember?.color,
        initials: updatedMember?.initials
      });
      setFamilyMembers(prev => 
        prev?.map(member => 
          member?.id === updatedMember?.id ? updatedMember : member
        )
      );
      setHasChanges(true);
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleMemberDelete = async (memberId) => {
    if (familyMembers?.length <= 1) {
      alert('Cannot delete the last family member');
      return;
    }
    
    if (!isSessionUnlocked()) {
      alert('Please unlock parental lock to delete members');
      return;
    }
    
    const confirmDelete = window.confirm(
      'Are you sure you want to delete this member? This will remove all their assignments from calendar, daily tasks, and long-term planning.'
    );
    
    if (confirmDelete) {
      try {
        await familyService?.removeFamilyMember(memberId);
        setFamilyMembers(prev => prev?.filter(member => member?.id !== memberId));
        setHasChanges(true);
      } catch (err) {
        setError(err?.message);
      }
    }
  };

  const handleAddMember = (newMember) => {
    const nextId = Math.max(...familyMembers?.map(m => m?.id)) + 1;
    setFamilyMembers(prev => [...prev, { ...newMember, id: nextId }]);
    setShowAddModal(false);
    setHasChanges(true);
  };

  const handlePreferencesChange = async (updatedPreferences) => {
    if (!familyGroup) return;
    
    try {
      await familyService?.updateFamilyPreferences(familyGroup?.id, {
        notifications: updatedPreferences?.notifications,
        default_view: updatedPreferences?.defaultView,
        auto_sync: updatedPreferences?.autoSync,
        week_starts_on: updatedPreferences?.weekStartsOn,
        google_calendar_connected: updatedPreferences?.googleCalendarConnected,
        google_calendar_id: updatedPreferences?.googleCalendarId,
        google_calendar_auto_import: updatedPreferences?.googleCalendarAutoImport,
        google_calendar_two_way_sync: updatedPreferences?.googleCalendarTwoWaySync,
        holiday_location: updatedPreferences?.holidayLocation,
        show_public_holidays: updatedPreferences?.showPublicHolidays,
        show_school_terms: updatedPreferences?.showSchoolTerms
      });
      setPreferences(updatedPreferences);
      setHasChanges(true);
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleSaveAll = () => {
    setSaveStatus('saving');
    
    // Simulate save operation
    setTimeout(() => {
      setSaveStatus('success');
      setHasChanges(false);
      
      setTimeout(() => {
        setSaveStatus(null);
      }, 3000);
    }, 1000);
  };

  const handleRestore = (data) => {
    if (data?.familyMembers) {
      setFamilyMembers(data?.familyMembers);
    }
    if (data?.preferences) {
      setPreferences(data?.preferences);
    }
    setHasChanges(true);
    setSaveStatus(null);
    alert('Configuration restored successfully! Click "Save All Changes" to apply.');
  };

  const [activeTab, setActiveTab] = useState('calendar');

  // Wrap sensitive sections with parental lock
  const renderContent = () => {
    // Calendar sync - protect
    if (activeTab === 'calendar') {
      const content = (
        <GoogleCalendarSettings
          preferences={preferences}
          onChange={handlePreferencesChange}
        />
      );
      
      return isUnlocked ? content : (
        <ParentalLockOverlay message="Calendar sync settings are protected. Enter PIN to make changes.">
          {content}
        </ParentalLockOverlay>
      );
    }

    // Backup & Restore - protect
    if (activeTab === 'backup') {
      const content = (
        <BackupRestoreControls
          familyMembers={familyMembers}
          preferences={preferences}
          onRestore={handleRestore}
        />
      );
      
      return isUnlocked ? content : (
        <ParentalLockOverlay message="Backup and restore operations are protected. Enter PIN to proceed.">
          {content}
        </ParentalLockOverlay>
      );
    }

    // Other tabs render normally
    if (activeTab === 'achievements') {
      return (
        <AchievementsPanel
          familyMembers={familyMembers}
          memberAchievements={memberAchievements}
          allTasks={allTasks}
        />
      );
    }

    if (activeTab === 'parental-lock') {
      return <ParentalLockSettings />;
    }

    return null;
  };

  return (
    <>
      <Helmet>
        <title>Family Settings - Starling Schedule</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-h1 font-heading text-foreground mb-2">
                  Family Settings
                </h1>
                <p className="text-muted-foreground">
                  Manage family member profiles, colors, and system preferences with real-time synchronization
                </p>
              </div>
              
              <div className="flex items-center gap-3">
                <Button
                  variant="default"
                  size="lg"
                  iconName="save"
                  onClick={handleSaveAll}
                  disabled={!hasChanges || saveStatus === 'saving'}
                  loading={saveStatus === 'saving'}
                >
                  {saveStatus === 'success' ? 'Saved!' : 'Save All Changes'}
                </Button>
              </div>
            </div>
          </div>

          {/* Save Status Banner */}
          {saveStatus === 'success' && (
            <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-lg flex items-center gap-3">
              <Icon name="check-circle" size={20} className="text-success" />
              <span className="text-success font-medium">
                All changes synchronized across calendar, daily tasks, and long-term planning
              </span>
            </div>
          )}

          {/* Tab Navigation */}
          <TabNavigation
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />

          {/* Render tab content */}
          {renderContent()}
        </div>

        {/* Add Member Modal */}
        {showAddModal && (
          <AddMemberModal
            onClose={() => setShowAddModal(false)}
            onAdd={handleAddMember}
            existingMembers={familyMembers}
          />
        )}
      </div>
    </>
  );
};

export default FamilySettingsManagement;